import { Alert, Button, Divider, Modal } from 'antd';
import { Fragment, useEffect } from 'react';

import styles from './SpacesContactsSaveModal.module.scss';
import { setSpacesContactsSaveModalOpen } from './spacesContactsSaveModalStore';
import type { SpaceContactSummary } from './summaries';

type Props = {
  open: boolean;
  variant: 'admin' | 'extranet';
  summaries: SpaceContactSummary[];
  isLoading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

export const SpacesContactsSaveModal = ({
  open,
  variant,
  summaries,
  isLoading,
  onConfirm,
  onCancel,
}: Props) => {
  useEffect(() => {
    setSpacesContactsSaveModalOpen(open);

    return () => setSpacesContactsSaveModalOpen(false);
  }, [open]);

  return (
    <Modal
      open={open}
      title="Apply your changes?"
      maskClosable={!isLoading}
      closable={!isLoading}
      onCancel={onCancel}
      centered
      className={styles.modal}
      footer={[
        <Button key="back" type="ghost" disabled={isLoading} onClick={onCancel}>
          Back to edit
        </Button>,
        <Button
          key="confirm"
          type="primary"
          loading={isLoading}
          onClick={onConfirm}
        >
          I understand, confirm changes
        </Button>,
      ]}
    >
      <div className={styles.blocks}>
        {summaries.map((summary, index) => (
          <Fragment key={summary.spaceId}>
            {index > 0 && <Divider className={styles.divider} />}
            <div className={styles.block}>
              {summary.coveringExperiences.length > 0 ? (
                <>
                  <Alert
                    type="success"
                    showIcon
                    message={`Booking confirmations will be enabled for ${summary.spaceLabel}`}
                  />
                  <p className={styles.detail}>
                    Booking confirmations will be sent to{' '}
                    <span className={styles.email}>
                      {summary.emails.join(', ')}
                    </span>{' '}
                    {summary.coveringExperiences.length === 1
                      ? 'whenever a booking includes this activity:'
                      : `whenever a booking includes at least one of these ${summary.coveringExperiences.length} activities:`}
                  </p>
                  <ul className={styles.activityList}>
                    {summary.coveringExperiences.map((experience) => (
                      <li key={experience.id}>{experience.label}</li>
                    ))}
                  </ul>
                </>
              ) : (
                <>
                  <Alert
                    type="warning"
                    showIcon
                    message={`No activities linked to ${summary.spaceLabel}`}
                  />
                  <p className={styles.detail}>
                    No booking confirmations will be sent to{' '}
                    <span className={styles.email}>
                      {summary.emails.join(', ')}
                    </span>{' '}
                    yet.
                    <br />
                    {variant === 'admin' ? (
                      <>
                        <strong>
                          {' '}
                          Link at least one activity to {summary.spaceLabel}
                        </strong>{' '}
                        to enable booking confirmation emails.
                      </>
                    ) : (
                      <>
                        <strong>
                          Contact your Account Manager to link activities to{' '}
                          {summary.spaceLabel}
                        </strong>{' '}
                        to enable booking confirmation emails.
                      </>
                    )}
                  </p>
                </>
              )}
            </div>
          </Fragment>
        ))}
      </div>
    </Modal>
  );
};
